import { useRouter } from 'next/router';
import { type RefObject, useLayoutEffect, useRef } from 'react';

interface ScrollRestoration {
  ref?: RefObject<HTMLElement>;
  enabled?: boolean;
}

export const useScrollRestoration = (
  { ref, enabled = true }: ScrollRestoration = { ref: undefined, enabled: true }
) => {
  const router = useRouter();
  const isPopStateRef = useRef(false);
  const pendingScrollRef = useRef<{ x: number; y: number } | null>(null);

  const scrollKey = (path: string) => `scrollPos_${path}`;

  const saveScrollPos = (path: string) => {
    const scrollPos = ref?.current
      ? { x: ref.current.scrollLeft, y: ref.current.scrollTop }
      : { x: window.scrollX, y: window.scrollY };
    sessionStorage.setItem(path, JSON.stringify(scrollPos));
  };

  const getSavedScrollPos = (path: string) => {
    const savedPos = sessionStorage.getItem(path);
    if (!savedPos) return null;
    return JSON.parse(savedPos);
  };

  const applyScrollPosition = (x: number, y: number) => {
    const targetElement = ref?.current || window;
    requestAnimationFrame(() => {
      targetElement.scrollTo(x, y);
    });
  };

  useLayoutEffect(() => {
    if (enabled && pendingScrollRef.current) {
      const { x, y } = pendingScrollRef.current;
      applyScrollPosition(x, y);
      pendingScrollRef.current = null;
    }
  });

  useLayoutEffect(() => {
    if (!enabled) return;

    if (!('scrollRestoration' in window.history)) return;

    window.history.scrollRestoration = 'manual';
    const currentPath = scrollKey(router.asPath);

    const onRouteChangeStart = () => {
      if (!isPopStateRef.current) {
        saveScrollPos(currentPath);
      }
    };

    const onRouteChangeComplete = () => {
      const newPath = scrollKey(router.asPath);

      if (isPopStateRef.current) {
        const savedPos = getSavedScrollPos(newPath);
        if (savedPos) {
          pendingScrollRef.current = savedPos;
        }
        isPopStateRef.current = false;
      } else {
        pendingScrollRef.current = { x: 0, y: 0 };
      }
    };

    router.beforePopState(() => {
      isPopStateRef.current = true;
      return true;
    });

    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [ref, router.asPath, enabled]);
};
